import {
  ChevronLeftIcon,
  ExportIcon,
  SortDescendingIcon,
} from 'components/icons';
import { ShowRowsSelector } from 'components/segments';
import { LoadingSpinner, Skeleton, Tooltip } from 'components/ui';
import { PAID_PLAN_TOOLTIP, subscriptionAccess } from 'config.js';
import { format, formatDistanceToNow, parseISO } from 'date-fns';
import querystring from 'querystring';
import { MouseEventHandler, useMemo, useState } from 'react';
import { useTable } from 'react-table';
import { useAppSelector } from 'store';
import { composableFiltersState } from 'store/composableFiltersSlice';
import {
  useExportCustomersListMutation,
  useGetCustomersListQuery,
  useGetStoreInfoQuery,
} from 'store/services/segmentsApi';
import { useParseURLQueryParams } from 'utils';
import { getCurrencySymbol } from 'utils/helpers';
import { useStoreSubscriptionPlan } from 'utils/useStoreSubscriptionPlan';
import { SortBySelector } from './SortBySelector';

export default function ListOfCustomers() {
  const [pageSize, setPageSize] = useState(20);
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState('CLV');
  const sortableColumns = ['clv', 'signed_up', 'orders', 'total_spent'];
  const pageSizeOptions = [10, 25, 50, 100];

  const { rawQueryParams } = useParseURLQueryParams();

  const { storeSubscriptionPlan } = useStoreSubscriptionPlan();

  const [
    exportUsersToFile,
    { isLoading: fileDownloading },
  ] = useExportCustomersListMutation();

  const storeInfoQueryParams = querystring.stringify({
    store_name: rawQueryParams?.store_name,
  });

  const { data: storeInfo } = useGetStoreInfoQuery(storeInfoQueryParams, {
    skip: !!!rawQueryParams?.store_name,
  });
  const currency = storeInfo?.currency || 'usd';
  const locale = storeInfo?.primary_locale ?? 'en';

  const queryParams = querystring.stringify({
    page_size: pageSize,
    page: page,
    sort_by: sortBy.toLowerCase(),
    ...rawQueryParams,
  });

  const {
    output: { filtersApiPayload },
    applied,
  } = useAppSelector(composableFiltersState);

  const { data: customerInfo, isFetching: loading } = useGetCustomersListQuery(
    {
      serializedParams: queryParams,
      body: filtersApiPayload,
    },
    { skip: !!!rawQueryParams?.store_name }
  );

  const currentSegment = applied.segment;

  const loadingStateDataPlaceholder = useMemo(() => {
    const rows = {
      email: '-',
      clv: '-',
      total_spent: '-',
      orders_count: '-',
      last_order: new Date().toISOString(),
      last_order_dt: new Date().toISOString(),
      created_at: new Date().toISOString(),
    };
    return [...Array(21)].map(() => rows);
  }, []);

  const data = useMemo(() => {
    if (loading) return loadingStateDataPlaceholder;
    if (customerInfo?.persons) return customerInfo.persons;
    if (!!!loading && !!!customerInfo?.persons) return [];
  }, [customerInfo?.persons, loading, loadingStateDataPlaceholder]);

  const columns = useMemo(
    () => [
      {
        Header: () => <p className="pl-3 text-left">Email</p>,
        accessor: 'email',
        className: 'justify-start cursor-none',

        Cell: ({ value }: { value: any }) => (
          <p className="pl-3 text-left">{value && String(value)}</p>
        ),
      },
      {
        Header: () => <p>CLV ({getCurrencySymbol(locale, currency)})</p>,
        accessor: 'clv',
        className: 'justify-end',

        Cell: ({ value }: { value: any }) => (
          <p>
            {(value || '--').toLocaleString(locale, {
              style: 'currency',
              currency: currency || 'USD',
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            })}
          </p>
        ),
      },
      {
        Header: 'Total spent',
        accessor: 'total_spent',
        className: 'justify-end',

        Cell: ({ value }: { value: any }) => (
          <p>
            {(value || '--').toLocaleString(locale, {
              style: 'currency',
              currency: currency || 'USD',
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            })}
          </p>
        ),
      },

      {
        Header: 'Orders',
        accessor: 'orders_count',
        className: 'justify-end',
      },
      {
        Header: 'Last ordered',
        id: 'last_order',
        className: 'justify-end cursor-none',

        Cell: ({ row }: { row: any }) => (
          <p>
            {row.values.last_order_dt &&
              formatDistanceToNow(parseISO(row.values.last_order_dt), {
                addSuffix: true,
              })}
          </p>
        ),
      },

      {
        Header: 'Last ordered (date)',
        accessor: 'last_order_dt',
        className: 'justify-end cursor-none',

        Cell: ({ value }: { value: any }) => (
          <p>{value && format(parseISO(value), 'yyyy-MM-dd')}</p>
        ),
      },
      {
        Header: 'Signed up',
        accessor: 'created_at',
        className: 'justify-end',

        Cell: ({ value }: { value: any }) => (
          <p>{value && format(parseISO(value), 'yyyy-MM-dd')}</p>
        ),
      },
      //TODO Disabled until implemented
      // {
      //   Header: '',
      //   accessor: 'view',
      //   className: 'justify-end cursor-none',
      //   Cell: () => (
      //     <Tooltip text="Coming soon">
      //       <div className="flex justify-end pr-3 opacity-50">
      //         <button
      //           aria-label="View customer history"
      //           className="flex items-center justify-center w-6 h-6 rounded bg-segments-gray focus:outline-none"
      //         >
      //           <ViewIcon className="w-4 h-4" />
      //         </button>
      //       </div>
      //     </Tooltip>
      //   ),
      // },
    ],
    [currency, locale]
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    // @ts-ignore
  } = useTable({ columns, data });

  if (!!!loading && !!!data?.length) return null;

  const prepareColumnNamesSortByFormatting = (columnName: string) => {
    return columnName
      .toLowerCase()
      .replace('_dt', '')
      .replace('_count', '')
      .replace('created_at', 'signed_up');
  };

  const handleExport = (content: string) => {
    const queryParams =
      rawQueryParams?.store_name &&
      querystring.stringify({
        export_format: 'csv',
        export_content: content,
        store_name: rawQueryParams?.store_name,
      });

    exportUsersToFile({
      serializedParams: String(queryParams),
      body: currentSegment?.json_filter
        ? currentSegment?.json_filter
        : filtersApiPayload,
    });
  };

  return (
    <section className="mt-7 md:mt-16">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="text-base tracking-wide md:text-2xl ">
            List of customers
          </h4>
          <p className="mt-2 text-black text-opacity-50 text-segments-13px">
            Showing {pageSize * page - pageSize + 1 || '...'}-
            {pageSize * page || '...'} of{' '}
            {customerInfo?.users?.toLocaleString(locale) || '...'} customers
          </p>
        </div>
        <div className="flex items-center space-x-8">
          <SortBySelector
            values={sortableColumns}
            selected={sortBy}
            setSelected={setSortBy}
          />
          <ShowRowsSelector
            values={pageSizeOptions}
            selected={pageSize}
            setSelected={setPageSize}
          />
          <Tooltip
            text={PAID_PLAN_TOOLTIP}
            placement="auto"
            disabled={
              !!!(
                storeSubscriptionPlan < subscriptionAccess.exportListOfCustomers
              )
            }
          >
            <button
              className="p-3 rounded-full bg-segments-gray focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
              onClick={() => handleExport('tresl')}
              disabled={
                storeSubscriptionPlan < subscriptionAccess.exportListOfCustomers
              }
            >
              {fileDownloading ? (
                <LoadingSpinner />
              ) : (
                <ExportIcon className="w-5 h-5 text-black" />
              )}
            </button>
          </Tooltip>
        </div>
      </div>

      <table
        {...getTableProps()}
        className="w-full mt-6 table-auto text-segments-13px"
      >
        <thead className="border-b border-black">
          {headerGroups.map((headerGroup) => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => {
                return (
                  <th
                    {...column.getHeaderProps()}
                    className={`py-2.5 px-1 ${
                      sortBy.toLowerCase() ===
                        prepareColumnNamesSortByFormatting(column.id) &&
                      'bg-segments-accent-color-1 bg-opacity-5 text-segments-link'
                    }`}
                  >
                    <span
                      // @ts-ignore
                      className={`flex items-end ${column.className}`}
                    >
                      {column.render('Header')}
                      {sortBy.toLowerCase() ===
                        prepareColumnNamesSortByFormatting(column.id) && (
                        <SortDescendingIcon className="w-4 h-4 ml-1" />
                      )}
                    </span>
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()} className="text-base">
          {rows.map((row) => {
            prepareRow(row);
            return (
              <tr
                {...row.getRowProps()}
                className="border-b odd:bg-segments-gray-F1 hover:bg-segments-accent-green-3"
              >
                {row.cells.map((cell) => {
                  return (
                    <td
                      {...cell.getCellProps()}
                      className={`py-2 text-right px-1 ${
                        sortBy.toLowerCase() ===
                          prepareColumnNamesSortByFormatting(cell.column.id) &&
                        'bg-segments-accent-color-1 bg-opacity-5 text-segments-link'
                      }`}
                    >
                      <Skeleton isLoaded={!!!loading}>
                        {cell.render('Cell')}
                      </Skeleton>
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
      <PaginationComponent
        pages={customerInfo?.pages}
        currentPage={page}
        setPage={setPage}
      />
    </section>
  );
}

function PaginationComponent({
  pages,
  currentPage,
  setPage,
}: {
  pages: Array<any> | undefined;
  currentPage: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
}) {
  const smallestPage = pages ? Math.min(...pages.filter((v) => v !== null)) : 1;

  const largestPage = Math.max(...(pages || [].filter((v) => v !== null)));

  const handlePageChange = (e: any) => {
    setPage(Number(e.target.innerText));
  };

  const handlePreviousPage = () => {
    if (currentPage > smallestPage) {
      setPage(currentPage - 1);
    }
  };
  const handleNextPage = () => {
    if (currentPage < largestPage) {
      setPage(currentPage + 1);
    }
  };

  const { storeSubscriptionPlan } = useStoreSubscriptionPlan();

  const subscriptionDisabled =
    storeSubscriptionPlan < subscriptionAccess.listOfCustomersPagination;

  return (
    <Tooltip
      text={PAID_PLAN_TOOLTIP}
      disabled={
        !!!(
          storeSubscriptionPlan < subscriptionAccess.listOfCustomersPagination
        )
      }
    >
      <div className="flex justify-center mt-8">
        <button
          aria-label="Previous page"
          className="flex items-center justify-center w-8 h-8 rounded hover:bg-segments-gray-F1 disabled:cursor-not-allowed focus:outline-none disabled:opacity-50"
          onClick={handlePreviousPage}
          disabled={subscriptionDisabled || currentPage === smallestPage}
        >
          <ChevronLeftIcon className="w-4 h-4" />
        </button>
        {pages?.map((page) => (
          <PaginationNumberTiles
            pageNumber={page}
            selected={page === currentPage}
            setPage={handlePageChange}
            key={page}
            disabled={subscriptionDisabled}
          />
        ))}
        <button
          aria-label="Next page"
          className="flex items-center justify-center w-8 h-8 rounded hover:bg-segments-gray-F1 disabled:cursor-not-allowed focus:outline-none disabled:opacity-50"
          onClick={handleNextPage}
          disabled={subscriptionDisabled || currentPage === largestPage}
        >
          <ChevronLeftIcon className="w-4 h-4 transform rotate-180" />
        </button>
      </div>
    </Tooltip>
  );
}

function PaginationNumberTiles({
  pageNumber,
  selected,
  setPage,
  disabled,
}: {
  pageNumber: number;
  selected: boolean;
  setPage: MouseEventHandler<HTMLButtonElement>;
  disabled: boolean;
}) {
  return (
    <button
      className={`rounded flex items-center justify-center py-1 px-3 focus:outline-none hover:bg-segments-gray-F1 disabled:cursor-not-allowed disabled:opacity-50 ${
        !!!pageNumber && 'pointer-events-none'
      } ${
        selected &&
        'bg-segments-accent-color-1 text-white hover:bg-segments-accent-color-1'
      }`}
      disabled={disabled || !pageNumber}
      onClick={setPage}
    >
      {pageNumber ? pageNumber.toLocaleString() : '...'}
    </button>
  );
}
